/**
 * Historical session page pull (v0.3.0, task-50/51) — pure client helpers.
 *
 * The realtime `session.tail` push only carries the newest subset; the phone can
 * request older pages with `session.tail.request` and receives a
 * `session.tail.response` (`SessionTail` shape, including `next_cursor`). Errors
 * arrive as the generic `event` payload `session.tail.error`. This module keeps
 * the pagination state machine, merge (dedupe/order), error copy and the demo
 * fallback offline-testable; no network or event text logging happens here.
 */
import {
  SESSION_TAIL_REQUEST_DEFAULT_LIMIT,
  SESSION_TAIL_REQUEST_MAX_LIMIT,
  isSessionTailRequest,
  type SessionTailRequest,
} from '@luminis/protocol';
import { parseSessionTail, type ParsedSessionTail } from './live/reducers';
import { mergeSessionEvents } from './sessions';
import type { SessionEventView, SessionView } from './types';

/** Fixed generic event name for tail-pull failures (no new MessageType). */
export const SESSION_TAIL_ERROR_EVENT = 'session.tail.error';

/** Per-request client timeout (server pages are bounded; 20s is generous). */
export const SESSION_TAIL_REQUEST_TIMEOUT_MS = 20_000;

/** Bound kept by the detail timeline when older pages are merged in. */
export const MAX_SESSION_HISTORY_EVENTS = 2000;

export const SESSION_TAIL_ERROR_MESSAGES: Record<string, string> = {
  INVALID_REQUEST: '历史请求无效（游标或参数不被接受）',
  NOT_FOUND: '会话历史不存在或已被清理',
  INTERNAL: 'daemon 无法读取该会话历史，请稍后重试',
};

export const SESSION_TAIL_ERROR_FALLBACK = '加载历史失败，请稍后重试';
export const SESSION_TAIL_TIMEOUT_MESSAGE = '加载历史超时，未收到响应；请稍后重试';

export function sessionTailErrorCopy(code: unknown): string {
  const normalized = typeof code === 'string' ? code.toUpperCase() : '';
  return SESSION_TAIL_ERROR_MESSAGES[normalized] ?? SESSION_TAIL_ERROR_FALLBACK;
}

/** Clamp a page-size hint to the daemon contract (default 100, 1..200). */
export function normalizeTailLimit(limit: number | undefined): number {
  if (typeof limit !== 'number' || !Number.isSafeInteger(limit) || limit < 1) {
    return SESSION_TAIL_REQUEST_DEFAULT_LIMIT;
  }
  return Math.min(limit, SESSION_TAIL_REQUEST_MAX_LIMIT);
}

export interface SessionTailError {
  code: string;
  message: string;
}

/** Parse the generic `event` envelope carrying a `session.tail.error`. */
export function parseSessionTailErrorEvent(payload: unknown): SessionTailError | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidate = payload as Record<string, unknown>;
  if (candidate['event'] !== SESSION_TAIL_ERROR_EVENT) return null;
  const data = candidate['data'];
  if (typeof data !== 'object' || data === null) return null;
  const detail = data as Record<string, unknown>;
  if (typeof detail['code'] !== 'string') return null;
  const code = detail['code'];
  return {
    code,
    // Never trust server text for display: fixed copy is chosen by code.
    message: sessionTailErrorCopy(code),
  };
}

/**
 * Earliest event timestamp currently on screen (history pull anchor). The
 * timeline is sorted, but scanning makes this robust against unsorted inputs.
 */
export function earliestSessionEventTs(events: readonly SessionEventView[]): number | undefined {
  let earliest: number | undefined;
  for (const event of events) {
    if (!Number.isFinite(event.ts)) continue;
    if (earliest === undefined || event.ts < earliest) earliest = event.ts;
  }
  return earliest;
}

/**
 * Build a validated request payload; returns null for invalid input.
 *
 * Reverse pagination (v0.3.0-dev.5): the FIRST page carries
 * `before_ts = earliest displayed event ts` (no cursor) so the daemon returns
 * the newest page just older than what the phone has. Later pages pass the
 * opaque `next_cursor` (`h:<seq>`) back unchanged; `before_ts` is ignored when a
 * cursor is present (server contract).
 */
export function buildSessionTailRequest(input: {
  source: string;
  sessionId: string;
  cursor?: string;
  beforeTs?: number;
  limit?: number;
}): SessionTailRequest | null {
  const request: Record<string, unknown> = {
    source: input.source,
    session_id: input.sessionId,
    limit: normalizeTailLimit(input.limit),
  };
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    // Opaque cursor: never parse/rewrite it.
    request['cursor'] = input.cursor;
  } else if (
    typeof input.beforeTs === 'number' &&
    Number.isFinite(input.beforeTs) &&
    input.beforeTs > 0
  ) {
    request['before_ts'] = Math.floor(input.beforeTs);
  }
  return isSessionTailRequest(request) ? request : null;
}

export interface SessionHistoryState {
  /** At least one page request was attempted (controls “加载更早” copy). */
  started: boolean;
  loading: boolean;
  /** No `next_cursor` on the last page → “已到最早”. */
  exhausted: boolean;
  /** Cursor to send with the next request (undefined for the first page). */
  nextCursor?: string;
  /** Redacted, user-safe error copy. */
  error?: string;
  /** Local timestamp (seconds) of the last successful page. */
  lastLoadedAt?: number;
}

export const INITIAL_SESSION_HISTORY_STATE: SessionHistoryState = {
  started: false,
  loading: false,
  exhausted: false,
};

export type SessionHistoryAction =
  | { type: 'start'; now: number }
  | { type: 'success'; nextCursor?: string; now: number }
  | { type: 'error'; error: string; now: number }
  | { type: 'reset' };

/** Small pure state machine; one request at a time (double-start is ignored). */
export function reduceSessionHistory(
  state: SessionHistoryState,
  action: SessionHistoryAction,
): SessionHistoryState {
  switch (action.type) {
    case 'start':
      if (state.loading || state.exhausted) return state;
      return { ...state, started: true, loading: true, error: undefined };
    case 'success':
      return {
        started: true,
        loading: false,
        exhausted: action.nextCursor === undefined,
        ...(action.nextCursor !== undefined ? { nextCursor: action.nextCursor } : {}),
        lastLoadedAt: action.now,
      };
    case 'error':
      return {
        ...state,
        started: true,
        loading: false,
        error: action.error,
      };
    case 'reset':
      return { ...INITIAL_SESSION_HISTORY_STATE };
    default:
      return state;
  }
}

export type SessionHistoryPageResult =
  | {
      ok: true;
      events: SessionEventView[];
      nextCursor?: string;
      exhausted: boolean;
    }
  | { ok: false; error: string };

/**
 * Merge a historical page with the realtime/snapshot events: dedupe by the
 * unified event key, chronological order, bounded memory. Older pages therefore
 * slot in before the newest events without duplicating anything.
 */
export function mergeHistoryEvents(
  existing: readonly SessionEventView[],
  incoming: readonly SessionEventView[],
  limit = MAX_SESSION_HISTORY_EVENTS,
): SessionEventView[] {
  return mergeSessionEvents(existing, incoming, limit);
}

/** Validate a `session.tail.response` against the request that is in flight. */
export function parseSessionHistoryResponse(
  payload: unknown,
  expected: { source: string; sessionId: string },
): SessionHistoryPageResult {
  const parsed: ParsedSessionTail | null = parseSessionTail(payload);
  if (!parsed) return { ok: false, error: '历史响应格式无效' };
  if (parsed.source !== expected.source || parsed.session_id !== expected.sessionId) {
    return { ok: false, error: '历史响应与会话不匹配' };
  }
  return {
    ok: true,
    events: parsed.events,
    ...(parsed.next_cursor !== undefined ? { nextCursor: parsed.next_cursor } : {}),
    exhausted: parsed.next_cursor === undefined,
  };
}

/**
 * Demo-mode fallback: deterministic older events so the history UI is usable
 * without a daemon. One synthetic page, no cursor (→ “已到最早”).
 */
export function demoEarlierSessionEvents(
  session: Pick<SessionView, 'last_event_at'>,
  count = 12,
): SessionEventView[] {
  const pageSize = Math.max(1, Math.min(Math.trunc(count), 50));
  const newest = session.last_event_at;
  const events: SessionEventView[] = [];
  for (let index = pageSize; index >= 1; index -= 1) {
    const ts = newest - index * 600;
    const kind: SessionEventView['kind'] = index % 3 === 0 ? 'status' : 'message';
    events.push({
      ts,
      kind,
      ...(kind === 'message' ? { role: index % 2 === 0 ? 'user' : 'assistant' } : {}),
      text:
        kind === 'status' ? '演示历史：会话已恢复' : '演示历史事件（本机 mock，不连接真实 daemon）',
    });
  }
  return events.sort((left, right) => left.ts - right.ts);
}
